import type {FeatureReferenceBinding, SerializedFeatureReferenceBinding} from '../binding/FeatureReferenceBinding';
import {createFeatureReferenceBinding} from '../binding/createFeatureReferenceBinding';
import {featureCommandAdapterRegistry} from '../command/FeatureCommandAdapterRegistry';
import { FeatureCommandDependencyIndex } from '../command/FeatureCommandDependencyIndex';

/**
 * 读取命令中的序列化绑定列表。
 * @param command 命令。
 * @returns 绑定载荷列表。
 */
function resolveSerializedBindingsFromCommand(command: CadCommand): SerializedFeatureReferenceBinding[] {
    const params = (command.params ?? {}) as Record<string, unknown>;
    const bindings = params.referenceBindings;
    if (!Array.isArray(bindings)) {
        return [];
    }
    return bindings.filter((binding): binding is SerializedFeatureReferenceBinding => {
        return typeof binding === 'object' && binding !== null && typeof Reflect.get(binding, 'kind') === 'string';
    });
}

/**
 * 生成绑定的稳定去重键。
 *
 * @param binding 特征引用绑定。
 * @returns 去重键。
 */
function resolveBindingKey(binding: FeatureReferenceBinding): string {
    return JSON.stringify(binding.serialize());
}

/**
 * 追加唯一绑定。
 *
 * @param target 目标绑定列表。
 * @param seenKeys 已出现的绑定键集合。
 * @param binding 候选绑定。
 */
function appendUniqueBinding(
    target: FeatureReferenceBinding[],
    seenKeys: Set<string>,
    binding: FeatureReferenceBinding,
): void {
    const key = resolveBindingKey(binding);
    if (seenKeys.has(key)) {
        return;
    }
    seenKeys.add(key);
    target.push(binding);
}

/**
 * 从命令历史恢复特征绑定。
 *
 * 设计说明：
 * 1. 新命令应优先携带 referenceBindings，这是依赖图的完整输入协议；
 * 2. 旧命令或简单特征可能只有 params.sketchId，必须复用 CommandAdapter/Prototype 的来源草图解析规则；
 * 3. 回退控制棒、删除计划、草图重建共享同一语义，不能在某个入口单独白名单命令字段。
 *
 * @param command 命令。
 * @returns 恢复出的绑定集合。
 */
function resolveFeatureBindingsFromCommand(command: CadCommand): FeatureReferenceBinding[] {
    const bindings: FeatureReferenceBinding[] = [];
    const seenKeys = new Set<string>();

    resolveSerializedBindingsFromCommand(command)
        .map((payload) => createFeatureReferenceBinding(payload))
        .forEach((binding) => appendUniqueBinding(bindings, seenKeys, binding));

    const prototype = featureCommandAdapterRegistry.createPrototypeFromCommand(command);
    prototype?.getBindings()
        .forEach((binding) => appendUniqueBinding(bindings, seenKeys, binding));

    const sourceSketchId = prototype?.getSourceSketchId();
    if (sourceSketchId && !bindings.some((binding) => binding.dependsOnSketch(sourceSketchId))) {
        appendUniqueBinding(bindings, seenKeys, createFeatureReferenceBinding({
            kind: 'sketch-profile',
            sketchId: sourceSketchId,
        }));
    }

    return bindings;
}

/**
 * 特征依赖图。
 */
export class FeatureDependencyGraph {
    /** 草图到特征 ID 的依赖索引。 */
    private readonly featureIdsBySketchId = new Map<string, Set<string>>();
    /** 特征到绑定集合的索引。 */
    private readonly bindingsByFeatureId = new Map<string, FeatureReferenceBinding[]>();
    /** 稳定模型引用到其直接消费者，不包含仅重算的范围依赖。 */
    private readonly featureIdsByModelReferenceId = new Map<string, Set<string>>();
    /** 重建闭包包含隐式范围依赖，但删除计划不消费此图。 */
    private rebuildIndex: FeatureCommandDependencyIndex | null = null;

    /**
     * 清空依赖图。
     */
    public clear(): void {
        this.featureIdsBySketchId.clear();
        this.bindingsByFeatureId.clear();
        this.featureIdsByModelReferenceId.clear();
        this.rebuildIndex = null;
    }

    /**
     * 基于命令历史重建依赖图。
     * @param commands 命令历史。
     */
    public rebuildFromCommands(commands: readonly CadCommand[]): void {
        this.clear();
        this.rebuildIndex = new FeatureCommandDependencyIndex({ commands, objects: [] });
        commands.forEach((command) => {
            const bindings = resolveFeatureBindingsFromCommand(command);
            if (bindings.length <= 0) {
                return;
            }

            this.bindingsByFeatureId.set(command.id, bindings);
            bindings.forEach((binding) => {
                binding.collectModelReferenceIds().forEach((referenceId) => {
                    const featureIds = this.featureIdsByModelReferenceId.get(referenceId) ?? new Set<string>();
                    featureIds.add(command.id);
                    this.featureIdsByModelReferenceId.set(referenceId, featureIds);
                });
                const serialized = binding.serialize();
                if (serialized.kind === 'extrude-reference' && serialized.target.kind === 'sketch-point') {
                    const featureIds = this.featureIdsBySketchId.get(serialized.target.sketchId) ?? new Set<string>();
                    featureIds.add(command.id);
                    this.featureIdsBySketchId.set(serialized.target.sketchId, featureIds);
                }
                if (serialized.kind === 'sketch-profile' || serialized.kind === 'sketch-sub-entity') {
                    const featureIds = this.featureIdsBySketchId.get(serialized.sketchId) ?? new Set<string>();
                    featureIds.add(command.id);
                    this.featureIdsBySketchId.set(serialized.sketchId, featureIds);
                }
            });
        });
    }

    /**
     * 获取依赖指定草图的特征 ID 列表。
     * @param sketchId 草图 ID。
     * @returns 特征 ID 列表。
     */
    public getDependentFeatureIds(sketchId: string): string[] {
        const directIds = Array.from(this.featureIdsBySketchId.get(sketchId) ?? []);
        return [...new Set(directIds.flatMap((id) => this.rebuildIndex?.resolveDependentFeatureIds(id, true) ?? [id]))].sort();
    }

    /** @param referenceId 基准面或模型的稳定身份。 @returns 重建消费者闭包。 */
    public getDependentModelReferenceFeatureIds(referenceId: string): string[] {
        return [...new Set(Array.from(this.featureIdsByModelReferenceId.get(referenceId) ?? [])
            .flatMap((id) => this.rebuildIndex?.resolveDependentFeatureIds(id, true) ?? [id]))];
    }

    /**
     * 获取指定特征的绑定集合。
     * @param featureId 特征 ID。
     * @returns 绑定集合。
     */
    public getBindingsByFeatureId(featureId: string): FeatureReferenceBinding[] {
        return (this.bindingsByFeatureId.get(featureId) ?? []).map((binding) => binding.clone());
    }

    /**
     * 判断指定草图是否存在下游特征依赖。
     * @param sketchId 草图 ID。
     * @returns 是否存在依赖。
     */
    public hasDependents(sketchId: string): boolean {
        return (this.featureIdsBySketchId.get(sketchId)?.size ?? 0) > 0;
    }
}
